// src/hooks/use-router.ts
import { useRouter as useNextRouter } from "next/navigation.js";
import { useRef } from "react";
import { isSameURL } from "@bprogress/core";
import { useProgress } from "@bprogress/react";
function removeFirstPathSegment(url) {
  const parts = url.pathname.split("/");
  if (parts.length > 1 && parts[1]) {
    parts.splice(1, 1);
    url.pathname = parts.join("/") || "/";
  }
  return url;
}
function useRouter(options) {
  const { customRouter, ...defaultProgressOptions } = options || {};
  const router = customRouter ? customRouter() : (
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useNextRouter()
  );
  const {
    start,
    stop,
    disableSameURL: providerDisableSameURL,
    startPosition: providerStartPosition,
    delay: providerDelay,
    stopDelay: providerStopDelay
  } = useProgress();
  const extendedRouterRef = useRef(null);
  function createHandler(fn) {
    return (href, options2) => {
      const {
        showProgress,
        startPosition,
        disableSameURL,
        basePath,
        i18nPath,
        delay,
        stopDelay,
        ...routerOpts
      } = options2 || {};
      const progressOpts = {
        ...defaultProgressOptions,
        showProgress,
        startPosition,
        disableSameURL,
        basePath,
        i18nPath,
        delay,
        stopDelay
      };
      const localDisableSameURL = progressOpts.disableSameURL !== void 0 ? progressOpts.disableSameURL : providerDisableSameURL;
      const localStartPosition = progressOpts.startPosition !== void 0 ? progressOpts.startPosition : providerStartPosition;
      const localDelay = progressOpts.delay !== void 0 ? progressOpts.delay : providerDelay;
      const localStopDelay = progressOpts.stopDelay !== void 0 ? progressOpts.stopDelay : providerStopDelay;
      if (progressOpts.showProgress === false) {
        return fn(href, routerOpts);
      }
      let currentUrl = new URL(location.href);
      const targetUrl = new URL(href, location.href);
      if (progressOpts.i18nPath) {
        currentUrl = removeFirstPathSegment(currentUrl);
      }
      if (progressOpts.basePath) {
        targetUrl.pathname = progressOpts.basePath + (targetUrl.pathname !== "/" ? targetUrl.pathname : "");
      }
      const sameURL = isSameURL(targetUrl, currentUrl);
      if (sameURL && localDisableSameURL) {
        return fn(href, routerOpts);
      }
      start(localStartPosition, localDelay);
      setTimeout(() => {
        if (sameURL) stop(localStopDelay);
      }, localDelay || 0);
      return fn(href, routerOpts);
    };
  }
  function createNoHrefHandler(fn) {
    return (options2) => {
      const {
        showProgress,
        startPosition,
        disableSameURL,
        basePath,
        i18nPath,
        delay,
        stopDelay,
        ...routerOpts
      } = options2 || {};
      const progressOpts = {
        ...defaultProgressOptions,
        showProgress,
        startPosition,
        disableSameURL,
        basePath,
        i18nPath,
        delay,
        stopDelay
      };
      const localStartPosition = progressOpts.startPosition !== void 0 ? progressOpts.startPosition : providerStartPosition;
      const localDelay = progressOpts.delay !== void 0 ? progressOpts.delay : providerDelay;
      const localStopDelay = progressOpts.stopDelay !== void 0 ? progressOpts.stopDelay : providerStopDelay;
      if (progressOpts.showProgress === false) {
        return fn(routerOpts);
      }
      start(localStartPosition, localDelay);
      const result = fn(routerOpts);
      setTimeout(() => {
        stop(localStopDelay);
      }, localDelay || 0);
      return result;
    };
  }
  function createPrefetchHandler(fn) {
    return (href, options2) => {
      return fn(href, options2);
    };
  }
  if (!extendedRouterRef.current) {
    extendedRouterRef.current = {
      ...router,
      push: createHandler(router.push),
      replace: createHandler(router.replace),
      prefetch: createPrefetchHandler(router.prefetch),
      back: createNoHrefHandler(router.back),
      refresh: createNoHrefHandler(router.refresh),
      forward: createNoHrefHandler(router.forward)
    };
  } else {
    extendedRouterRef.current.push = createHandler(router.push);
    extendedRouterRef.current.replace = createHandler(router.replace);
    extendedRouterRef.current.prefetch = createPrefetchHandler(router.prefetch);
    extendedRouterRef.current.back = createNoHrefHandler(router.back);
    extendedRouterRef.current.refresh = createNoHrefHandler(router.refresh);
    extendedRouterRef.current.forward = createNoHrefHandler(router.forward);
  }
  return extendedRouterRef.current;
}

// src/providers/app-progress-provider.tsx
import React2 from "react";

// src/components/app-progress.tsx
import {
  useAnchorProgress,
  withMemo
} from "@bprogress/react";
import { usePathname, useSearchParams } from "next/navigation.js";
var AppProgressComponent = (props) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  useAnchorProgress(props, [pathname, searchParams]);
  return null;
};
var AppProgress = withMemo(AppProgressComponent);
AppProgress.displayName = "AppProgress";

// src/providers/app-progress-provider.tsx
import { withSuspense } from "@bprogress/react";

// src/providers/next-progress-provider.tsx
import React from "react";
import {
  ProgressProvider
} from "@bprogress/react";
var NextProgressProvider = ({
  children,
  ProgressComponent,
  color,
  height,
  options,
  spinnerPosition,
  style,
  disableStyle,
  nonce,
  stopDelay,
  delay,
  startPosition,
  disableSameURL,
  shallowRouting,
  ...props
}) => {
  return /* @__PURE__ */ React.createElement(
    ProgressProvider,
    {
      color,
      height,
      options,
      spinnerPosition,
      style,
      disableStyle,
      nonce,
      stopDelay,
      delay,
      startPosition,
      disableSameURL,
      shallowRouting
    },
    /* @__PURE__ */ React.createElement(
      ProgressComponent,
      {
        stopDelay,
        delay,
        startPosition,
        disableSameURL,
        shallowRouting,
        ...props
      }
    ),
    children
  );
};

// src/providers/app-progress-provider.tsx
var SuspendedAppProgress = withSuspense(AppProgress);
var AppProgressProvider = ({
  children,
  ...props
}) => {
  return /* @__PURE__ */ React2.createElement(NextProgressProvider, { ProgressComponent: SuspendedAppProgress, ...props }, children);
};

// src/providers/pages-progress-provider.tsx
import React3 from "react";

// src/components/pages-progress.tsx
import { useEffect } from "react";
import { isSameURL as isSameURL2, isSameURLWithoutSearch } from "@bprogress/core";
import { useProgress as useProgress2, withMemo as withMemo2 } from "@bprogress/react";
import Router from "next/router.js";
var PagesProgressComponent = ({
  shallowRouting = false,
  disableSameURL = true,
  startPosition = 0,
  delay = 0,
  stopDelay = 0
}) => {
  const { start, stop } = useProgress2();
  useEffect(() => {
    const handleRouteStart = (url) => {
      const targetUrl = new URL(url, location.href);
      const currentUrl = new URL(location.href);
      if (shallowRouting && isSameURLWithoutSearch(targetUrl, currentUrl) && disableSameURL)
        return;
      if (isSameURL2(targetUrl, currentUrl) && disableSameURL) return;
      start(startPosition, delay);
    };
    const handleRouteDone = () => stop(stopDelay);
    Router.events.on("routeChangeStart", handleRouteStart);
    Router.events.on("routeChangeComplete", handleRouteDone);
    Router.events.on("routeChangeError", handleRouteDone);
    return () => {
      Router.events.off("routeChangeStart", handleRouteStart);
      Router.events.off("routeChangeComplete", handleRouteDone);
      Router.events.off("routeChangeError", handleRouteDone);
    };
  }, []);
  return null;
};
var PagesProgress = withMemo2(PagesProgressComponent);
PagesProgress.displayName = "PagesProgress";

// src/providers/pages-progress-provider.tsx
var PagesProgressProvider = ({
  children,
  ...props
}) => {
  return /* @__PURE__ */ React3.createElement(NextProgressProvider, { ProgressComponent: PagesProgress, ...props }, children);
};

// src/index.ts
import {
  useProgress as useProgress3,
  Progress,
  Bar,
  Peg,
  Spinner,
  SpinnerIcon,
  Indeterminate
} from "@bprogress/react";
export {
  AppProgressProvider,
  Bar,
  Indeterminate,
  PagesProgressProvider,
  Peg,
  Progress,
  Spinner,
  SpinnerIcon,
  useProgress3 as useProgress,
  useRouter
};
